import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  autoUpdate,
  flip,
  offset,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';

import * as actions from 'containers/App/actions';
import MyFocusComponent from './MyFocus';
import MyTasksComponent from './MyTasks';
import AllNotifications from './Notifications';
import './style.scss';

const FIRST_LOAD_COUNT = 100;
const NotificationsCenter = () => {
  const notifications = useSelector(state => state.global.notifications);
  const notificationCount = useSelector(state => state.global.notificationCount);
  const currentPage = useSelector(state => state.global.notificationsCurrentPage);
  const notificationsTotal = useSelector(state => state.global.notificationsTotal);
  const notificationPages = useSelector(state => state.global.notificationPages);
  const dispatch = useDispatch();
  const {
    getNotifications,
    getAddtNotifications,
    markNotificationsRead,
    markNotificationRead,
  } = bindActionCreators(actions, dispatch);

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setTabSelected] = useState('myFocus');
  const [hasMore, setHasMore] = useState(false);

  const handleDropdownClick = () => {
    // Reset scroll position when closing.
    if (!isOpen && scrollRef?.current) scrollRef.current.scrollTop = 0;
    setIsOpen(!isOpen);
    // This is to lead page 2 when notification center is opened.
    // We don't want to keep fetching more every time we open the
    // notification center. Will load more on scrolling.
    if (currentPage < 2) {
      fetchMore();
    }
  };

  const dismiss = () => {
    // Reset scroll position when closing.
    if (scrollRef?.current) scrollRef.current.scrollTop = 0;
    setIsOpen(false);
  };

  const handleTabChange = (tab) => {
    // Reset scroll position when changing tab.
    if (scrollRef?.current) scrollRef.current.scrollTop = 0;
    setTabSelected(tab);
  };

  useEffect(() => {
    setTabSelected('myFocus');
  }, [isOpen]);

  // Update notification pagination state.
  useEffect(() => {
    setHasMore(notificationsTotal > notifications?.length && currentPage < notificationPages);
  }, [notifications]);

  useEffect(() => {
    getNotifications({
      variables: {
        unreadOnly: false,
        page: 0,
        pageSize: FIRST_LOAD_COUNT,
      },
    });
  }, []);

  /**
   * First page index is 0, but the response returns index we send + 1. currentPage is
   * actually next page index for when we fetch for more notifications.
   */
  const fetchMore = () => {
    if (hasMore) {
      getAddtNotifications({
        variables: {
          unreadOnly: false,
          page: currentPage,
          pageSize: FIRST_LOAD_COUNT,
        },
      });
    }
  };

  // Infinite scroll functionality.
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!scrollRef?.current) {
      return;
    }

    const handleScroll = () => {
      const scrollTotal = scrollRef?.current?.clientHeight + scrollRef?.current?.scrollTop;
      const scrollPercentage = scrollTotal / scrollRef?.current?.scrollHeight;
      if (scrollPercentage > 0.75 && hasMore) {
        fetchMore();
        scrollRef.current?.removeEventListener('scroll', handleScroll);
      }
    };

    if (hasMore) {
      scrollRef.current.addEventListener('scroll', handleScroll);
    }

    return () => scrollRef.current?.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  const handleMarkAllNotifications = () => {
    markNotificationsRead({});
  };

  /*
  #TODO: When marking notification as read, we refetch page 1 of notifications.
  This resets notification query, e.g. if we have 200 notifications loaded and
  mark a notification as read, then we only load first 100 notifications again.
  This is fine for now, but need to figure out how to just update the
  notification from the current list stored in redux, instead of completely
  resetting our notification state.
  */
  const handleMarkNotificationRead = (id) => {
    const variables = {
      id,
    };
    markNotificationRead({ variables });
  };

  const isTabSelected = (option) => {
    return selectedTab === option ? 'selected' : '';
  };

  const renderSelectedComponent = () => {
    switch (selectedTab) {
      case 'myFocus':
        return (
          <MyFocusComponent
            notifications={notifications}
            markAllReads={handleMarkAllNotifications}
            markRead={handleMarkNotificationRead}
            dismiss={dismiss}
          />
        );
      case 'allMyTasks':
        return <MyTasksComponent />;
      case 'allNotifications':
        return (
          <AllNotifications
            notifications={notifications}
            markRead={handleMarkNotificationRead}
            dismiss={dismiss}
          />
        );
      default:
        return '';
    }
  };

  return (
    <Dropdown
      count={notificationCount}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      handleDropdownClick={handleDropdownClick}
    >
      <div className="notification-menu-sidebar">
        <ul>
          <li
            className={isTabSelected('myFocus')}
            onClick={() => handleTabChange('myFocus')}
          >
            My Focus
          </li>
          <li
            className={isTabSelected('allMyTasks')}
            onClick={() => handleTabChange('allMyTasks')}
          >
            All My Tasks
          </li>
          <li
            className={isTabSelected('allNotifications')}
            onClick={() => handleTabChange('allNotifications')}
          >
            All Notifications
          </li>
        </ul>
      </div>
      <div
        ref={scrollRef}
        className="notification-menu-selected-area"
      >
        {renderSelectedComponent()}
      </div>
    </Dropdown>
  );
};

const Dropdown = ({ count, isOpen, setIsOpen, handleDropdownClick, children }) => {
  const useDismissProps = { referencePressEvent: 'click', bubbles: false };
  const flipOptions = {
    fallbackPlacements: ['top', 'bottom'],
  };

  const show = () => {
    setIsOpen(true);
  };

  const dismiss = () => {
    setIsOpen(false);
  };

  const getClasses = (notificationsCount) => {
    if (notificationsCount > 0) {
      return 'badge-icon on';
    }
    return 'badge-icon';
  };

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    placement: 'bottom-end',
    middleware: [
      offset(0),
      flip(flipOptions),
    ],
    onOpenChange(opened, event, reason) {
      if (reason === 'reference-press') return;
      if (opened) {
        show(event);
      } else if (dismiss) {
        dismiss(event);
      }
    },
    whileElementsMounted: autoUpdate,
  });

  const close = useDismiss(context, useDismissProps);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    close,
  ]);

  return (
    <div
      ref={refs.setReference}
      className="notification-center"
      {...getReferenceProps()}
    >
      <button
        id="notification-toggle"
        className="dropdown-toggle notification-toggle button--unstyled"
        onClick={handleDropdownClick}
      >
        <span className={getClasses(count)}>
          {count}
        </span>
      </button>
      {isOpen && (
        <div
          ref={refs.setFloating}
          className="notification-menu dropdown-menu"
          id="notification-menu"
          style={{ ...floatingStyles }}
          {...getFloatingProps()}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default NotificationsCenter;
